import { useEffect, useRef } from 'react';
import ModalShell from './ui/ModalShell';

/**
 * Pravý side-panel pro formuláře/editory. Na desktopu slide-in zprava,
 * na mobilu standardní modal (`ModalShell`) — sheet zdola, v portálu nad
 * mobilní lištou. Dřív byl na mobilu full-screen přímo ve stromu stránky
 * a patička s Uložit mohla skončit pod plovoucí lištou.
 * Lehká varianta bez Radix/shadcn.
 *
 * Režimy:
 *  - modal=true (default): s backdropem, body scroll-lock, ESC zavírá.
 *    Blokuje pozadí → pro krátké úpravy, confirmy, kde je fokus 100% v panelu.
 *  - modal=false: non-modal "inspector" panel. Bez backdropu a bez scroll-locku,
 *    uživatel může dál klikat a scrollovat zbytek aplikace. Na mobilu stejně
 *    full-screen (tam modal/inspector rozlišení nedává smysl).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string|ReactNode
 *  - children: obsah (typicky form)
 *  - footer: volitelný footer (tlačítka); zůstává přilepený u spodku
 *  - isDark, isMobile: téma / viewport
 *  - width: šířka panelu na desktopu (default 560)
 *  - modal: boolean (default true)
 *  - topOffset: v inspector režimu Y offset od top viewportu (default 0).
 *    Předej výšku page headeru, pokud chceš, aby inspector "vyjížděl
 *    pod hlavičkou" a hlavička modulu zůstala viditelná.
 */
export default function SidePanel({ open, onClose, title, children, footer, isDark, isMobile, width = 560, modal = true, accent = null, topOffset = 0 }) {
    const panelRef = useRef(null);
    // Inspector (non-modal) režim používáme jen na desktopu — na mobilu je
    // fullscreen, kde necháváme modal chování (bez toho by overlay chyběl).
    const isInspector = !modal && !isMobile;

    useEffect(() => {
        // Mobil: Esc i zámek scrollu obstará ModalShell.
        if (!open || isMobile) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        if (!isInspector) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.removeEventListener('keydown', onKey);
                document.body.style.overflow = prevOverflow;
            };
        }
        return () => { document.removeEventListener('keydown', onKey); };
    }, [open, onClose, isInspector, isMobile]);

    // Push layout — inspector režim odsune hlavní obsah, aby se daly ovládat
    // obě okna najednou (panel + modul pod ním). Šířka panelu se promítne do
    // CSS proměnné --side-inspector-width, kterou čte <main> v app.jsx přes
    // paddingRight. Mobil / modal režim nic nenastavuje (var zůstane fallback 0).
    useEffect(() => {
        if (!open || !isInspector) return;
        const root = document.documentElement;
        root.style.setProperty('--side-inspector-width', `${width}px`);
        return () => { root.style.removeProperty('--side-inspector-width'); };
    }, [open, isInspector, width]);

    if (!open) return null;

    if (isMobile) {
        return (
            <ModalShell nadpis={title} onClose={onClose} isDark={isDark}
                barva={accent || undefined} paticka={footer}>
                {children}
                {!footer && <div aria-hidden="true" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />}
            </ModalShell>
        );
    }

    const backdrop = isInspector ? null : (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9998,
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                animation: 'sp-backdrop-in 0.2s ease-out',
            }}
        />
    );

    // Inspector režim respektuje topOffset (výška page headeru) — modul, kde
    // se inspector otevírá, předá rect.bottom své hlavičky a inspector vyjede
    // od tam dolů. topOffset=0 = od horní hrany viewportu (default fallback).
    const TOPBAR_HEIGHT = topOffset;
    // Inspector i modal používají var(--surface-raised) — odliší panel od
    // podkladu stránky (--surface-base), aby vizuálně skutečně "vyjížděl"
    // ze strany obrazovky a nesplýval s modulem pod ním.
    const panelBg = 'var(--surface-raised)';
    const panelStyle = {
        position: 'fixed',
        top: isInspector ? TOPBAR_HEIGHT : 0,
        right: 0, bottom: 0,
        // Inspector panel má nižší z-index, aby nezakrýval TopBar (50)
        // a důležité overlaye (toast/notifikace), ale byl nad běžným obsahem.
        zIndex: isInspector ? 40 : 9999,
        width: `min(${width}px, 100vw)`,
        background: panelBg,
        // Levý okraj a stín jasně oddělují panel od stránky — inspector
        // působí jako nepříhledný a "nadnášený" element, ne jako součást modulu.
        borderLeft: accent
            ? `1px solid ${accent}66`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        boxShadow: isInspector
            ? (isDark
                ? '-2px 0 12px rgba(0,0,0,0.30)'
                : '-2px 0 8px rgba(0,0,0,0.10)')
            : '-24px 0 48px rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column',
        animation: 'sp-slide-right 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
    };

    return (
        <>
            {backdrop}
            <aside ref={panelRef} role={isInspector ? 'complementary' : 'dialog'} aria-modal={isInspector ? undefined : 'true'} style={panelStyle}>
                {title && (
                    <header style={{
                        padding: '14px 20px',
                        borderBottom: accent
                            ? `1px solid ${accent}33`
                            : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                        // Jemný gradient na pozadí headeru laděný do barvy sekce,
                        // ať header vizuálně "patří" k tématu panelu.
                        background: accent
                            ? `linear-gradient(180deg, ${accent}0F, transparent)`
                            : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                        {typeof title === 'string'
                            ? <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h2>
                            : title}
                        <button type="button" onClick={onClose} aria-label="Zavřít" style={{
                            background: 'none', border: 'none', padding: 4, cursor: 'pointer',
                            color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)',
                            lineHeight: 0,
                        }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                        </button>
                    </header>
                )}
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    {children}
                </div>
                {footer && (
                    <div style={{
                        padding: '12px 20px',
                        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                        flexShrink: 0,
                    }}>
                        {footer}
                    </div>
                )}
            </aside>
        </>
    );
}
